import React, { useState, useEffect } from 'react';
import { productsApi, bundlesApi } from '../services/api';
import { FunnelIcon, CubeIcon } from '@heroicons/react/24/outline';
import { COUNTRIES } from '../utils/addressData';

interface Product {
  id: string;
  sku: string;
  title: string;
  barcode?: string;
  price: number;
  weight?: number;
  image_url?: string;
  variant_count?: number;
  requires_packing_verification?: boolean;
  hs_tariff_code?: string;
  country_of_origin?: string;
}

interface ProductVariant {
  id: string;
  title: string;
  sku: string;
  barcode?: string;
  price: number;
  weight?: number;
  option1?: string;
  option2?: string;
  option3?: string;
  inventory_quantity?: number;
  is_bundle?: boolean;
  requires_packing_verification?: boolean | null; // null = inherit from parent
}

interface BundleComponent {
  id: string;
  variant_id: string;
  sku: string;
  barcode: string;
  variant_title: string;
  product_title: string;
  quantity: number;
  image_url?: string;
}

export const Products: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());
  const [productVariants, setProductVariants] = useState<Record<string, ProductVariant[]>>({});
  
  // Edit modal state
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [editForm, setEditForm] = useState({ weight: '', barcode: '', hs_tariff_code: '', country_of_origin: '' });
  const [saving, setSaving] = useState(false);
  
  // Bundle configuration state
  const [configuringBundle, setConfiguringBundle] = useState<{
    variant: ProductVariant;
    product: Product;
  } | null>(null);
  const [bundleComponents, setBundleComponents] = useState<BundleComponent[]>([]);
  const [loadingBundle, setLoadingBundle] = useState(false);
  const [allVariants, setAllVariants] = useState<Array<{ id: string; sku: string; barcode?: string; title: string; productTitle: string }>>([]);
  const [selectedComponentId, setSelectedComponentId] = useState('');
  const [showVariantPicker, setShowVariantPicker] = useState(false);
  const [variantSearchQuery, setVariantSearchQuery] = useState('');
  const [componentQuantity, setComponentQuantity] = useState(1);
  const [savingBundle, setSavingBundle] = useState(false);
  const [bundleError, setBundleError] = useState<string | null>(null);

  // Load products on mount
  useEffect(() => {
    loadProducts();
  }, []);

  // Search with debouncing
  useEffect(() => {
    if (!searchQuery) {
      loadProducts();
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await productsApi.search(searchQuery);
        setProducts(data.products || []);
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setLoading(false);
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const data = await productsApi.list();
      setProducts(data.products || []);
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncProducts = async () => {
    setSyncing(true);
    try {
      const result = await productsApi.sync();
      alert(`Success! Synced ${result.details.added + result.details.updated} products\n\nAdded: ${result.details.added}\nUpdated: ${result.details.updated}`);
      await loadProducts(); // Reload products after sync
    } catch (error: any) {
      alert(`Sync failed: ${error.response?.data?.message || error.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const toggleProductSelection = (productId: string) => {
    setSelectedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const handleTogglePackingVerification = async (productId: string, requiresVerification: boolean) => {
    try {
      await productsApi.updatePackingVerification(productId, requiresVerification);
      setProducts(prev => prev.map(p =>
        p.id === productId ? { ...p, requires_packing_verification: requiresVerification } : p
      ));
      // Also update the modal's local copy so the checkbox reflects the new
      // value immediately without having to close and reopen the modal.
      setEditingProduct(prev =>
        prev && prev.id === productId
          ? { ...prev, requires_packing_verification: requiresVerification }
          : prev
      );
    } catch (error) {
      console.error('Failed to update packing verification:', error);
      alert('Failed to update packing verification setting');
    }
  };

  // Toggle variant packing verification: null (inherit) → false (no) → true (yes) → null
  const handleToggleVariantPackingVerification = async (productId: string, variantId: string, currentValue: boolean | null | undefined) => {
    // Cycle: null → false → true → null
    let newValue: boolean | null;
    if (currentValue === null || currentValue === undefined) {
      newValue = false; // inherit → no
    } else if (currentValue === false) {
      newValue = true; // no → yes
    } else {
      newValue = null; // yes → inherit
    }

    try {
      await productsApi.updateVariantPackingVerification(variantId, newValue);
      // Update local state
      setProductVariants(prev => ({
        ...prev,
        [productId]: prev[productId]?.map(v =>
          v.id === variantId ? { ...v, requires_packing_verification: newValue } : v
        ) || [],
      }));
    } catch (error) {
      console.error('Failed to update variant packing verification:', error);
      alert('Failed to update variant packing verification setting');
    }
  };

  const toggleProductExpansion = async (productId: string) => {
    const newExpanded = new Set(expandedProducts);

    if (newExpanded.has(productId)) {
      newExpanded.delete(productId);
    } else {
      newExpanded.add(productId);
      // Load variants if not already loaded
      if (!productVariants[productId]) {
        try {
          const data = await productsApi.get(productId);
          setProductVariants(prev => ({
            ...prev,
            [productId]: data.variants || []
          }));
        } catch (error) {
          console.error('Failed to load variants:', error);
        }
      }
    }

    setExpandedProducts(newExpanded);
  };

  // Open edit modal
  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setEditForm({
      weight: product.weight?.toString() || '',
      barcode: product.barcode || '',
      hs_tariff_code: product.hs_tariff_code || '',
      country_of_origin: product.country_of_origin || 'US',
    });
  };

  // Save product edits
  const handleSaveProduct = async () => {
    if (!editingProduct) return;
    
    setSaving(true);
    try {
      await productsApi.update(editingProduct.id, {
        weight: editForm.weight ? parseFloat(editForm.weight) : undefined,
        barcode: editForm.barcode || undefined,
        hs_tariff_code: editForm.hs_tariff_code || undefined,
        country_of_origin: editForm.country_of_origin || undefined,
      });
      
      // Update local state
      setProducts(prev => prev.map(p =>
        p.id === editingProduct.id 
          ? { 
              ...p, 
              weight: parseFloat(editForm.weight) || 0, 
              barcode: editForm.barcode,
              hs_tariff_code: editForm.hs_tariff_code,
              country_of_origin: editForm.country_of_origin,
            }
          : p
      ));
      
      setEditingProduct(null);
      alert('Product updated successfully!');
    } catch (error: any) {
      console.error('Failed to save product:', error);
      alert(`Failed to save: ${error.response?.data?.error || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Open bundle configuration modal
  const handleConfigureBundle = async (variant: ProductVariant, product: Product) => {
    setConfiguringBundle({ variant, product });
    setLoadingBundle(true);
    setSelectedComponentId('');
    setComponentQuantity(1);
    setBundleError(null);
    setAllVariants([]);
    
    try {
      // Load existing bundle components and all variants in parallel
      console.log('[Bundle] Loading bundle data for variant:', variant.id);
      
      const [bundleData, variantsData] = await Promise.all([
        bundlesApi.getComponents(variant.id),
        productsApi.listAllVariants(),
      ]);
      
      console.log('[Bundle] Bundle components:', bundleData);
      console.log('[Bundle] Variants data:', variantsData);
      console.log('[Bundle] Variants count:', variantsData?.variants?.length || 0);
      
      setBundleComponents(bundleData.components || []);
      
      // Filter out the current variant from the dropdown options
      const filteredVariants = (variantsData.variants || [])
        .filter((v: any) => v.id !== variant.id)
        .map((v: any) => ({
          id: v.id,
          sku: v.sku,
          barcode: v.barcode,
          title: v.title,
          productTitle: v.productTitle,
        }));
      
      console.log('[Bundle] Filtered variants:', filteredVariants.length);
      setAllVariants(filteredVariants);
      
      if (filteredVariants.length === 0) {
        setBundleError('No variants found. This could mean the API call failed or there are no products to select from.');
      }
    } catch (error: any) {
      console.error('[Bundle] Failed to load bundle data:', error);
      console.error('[Bundle] Error response:', error.response?.data);
      console.error('[Bundle] Error status:', error.response?.status);
      const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
      setBundleError(`Failed to load variants: ${errorMsg} (Status: ${error.response?.status || 'N/A'})`);
    } finally {
      setLoadingBundle(false);
    }
  };

  // Add a component to the bundle
  const handleAddBundleComponent = async () => {
    if (!configuringBundle || !selectedComponentId || componentQuantity < 1) return;
    
    setSavingBundle(true);
    try {
      await bundlesApi.create({
        bundle_variant_id: configuringBundle.variant.id,
        component_variant_id: selectedComponentId,
        quantity: componentQuantity,
      });
      
      // Refresh components
      const bundleData = await bundlesApi.getComponents(configuringBundle.variant.id);
      setBundleComponents(bundleData.components || []);
      
      // Update variant's is_bundle flag in local state
      setProductVariants(prev => {
        const productId = configuringBundle.product.id;
        if (!prev[productId]) return prev;
        return {
          ...prev,
          [productId]: prev[productId].map(v =>
            v.id === configuringBundle.variant.id ? { ...v, is_bundle: true } : v
          ),
        };
      });
      
      setSelectedComponentId('');
      setComponentQuantity(1);
    } catch (error: any) {
      console.error('Failed to add bundle component:', error);
      alert(error.response?.data?.error || 'Failed to add component');
    } finally {
      setSavingBundle(false);
    }
  };

  // Remove a component from the bundle
  const handleRemoveBundleComponent = async (bundleId: string) => {
    if (!configuringBundle) return;
    
    try {
      await bundlesApi.delete(bundleId);
      
      // Refresh components
      const bundleData = await bundlesApi.getComponents(configuringBundle.variant.id);
      setBundleComponents(bundleData.components || []);
      
      // Update is_bundle flag if no more components
      if (bundleData.components.length === 0) {
        setProductVariants(prev => {
          const productId = configuringBundle.product.id;
          if (!prev[productId]) return prev;
          return {
            ...prev,
            [productId]: prev[productId].map(v =>
              v.id === configuringBundle.variant.id ? { ...v, is_bundle: false } : v
            ),
          };
        });
      }
    } catch (error) {
      console.error('Failed to remove bundle component:', error);
      alert('Failed to remove component');
    }
  };

  // Print a product/variant label
  const handlePrintLabel = (sku: string, title: string, barcode?: string) => {
    // Create a printable label window
    const labelWindow = window.open('', '_blank', 'width=400,height=300');
    if (!labelWindow) {
      alert('Please allow popups to print labels');
      return;
    }
    
    labelWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Product Label - ${sku}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
            text-align: center;
          }
          .label {
            border: 2px solid #000;
            padding: 20px;
            width: 300px;
            margin: 0 auto;
          }
          .sku {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 10px;
          }
          .title {
            font-size: 14px;
            margin-bottom: 15px;
            color: #333;
          }
          .barcode {
            font-family: 'Libre Barcode 39', monospace;
            font-size: 48px;
            margin: 10px 0;
          }
          .barcode-text {
            font-size: 12px;
            font-family: monospace;
          }
          @media print {
            body { margin: 0; padding: 10px; }
            .no-print { display: none; }
          }
        </style>
        <link href="https://fonts.googleapis.com/css2?family=Libre+Barcode+39&display=swap" rel="stylesheet">
      </head>
      <body>
        <div class="label">
          <div class="sku">${sku}</div>
          <div class="title">${title}</div>
          ${barcode ? `
            <div class="barcode">*${barcode}*</div>
            <div class="barcode-text">${barcode}</div>
          ` : '<div style="color: #999; font-size: 12px;">No barcode</div>'}
        </div>
        <br/>
        <button class="no-print" onclick="window.print()">Print Label</button>
        <button class="no-print" onclick="window.close()">Close</button>
      </body>
      </html>
    `);
    labelWindow.document.close();
  };

  // Bulk print selected products
  const handleBulkPrintLabels = () => {
    const selectedProductData = products.filter(p => selectedProducts.includes(p.id));
    if (selectedProductData.length === 0) return;
    
    const labelWindow = window.open('', '_blank', 'width=600,height=800');
    if (!labelWindow) {
      alert('Please allow popups to print labels');
      return;
    }
    
    const labelsHtml = selectedProductData.map(p => `
      <div class="label" style="page-break-after: always;">
        <div class="sku">${p.sku}</div>
        <div class="title">${p.title}</div>
        ${p.barcode ? `
          <div class="barcode">*${p.barcode}*</div>
          <div class="barcode-text">${p.barcode}</div>
        ` : '<div style="color: #999; font-size: 12px;">No barcode</div>'}
      </div>
    `).join('<br/>');
    
    labelWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Product Labels (${selectedProductData.length})</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          .label {
            border: 2px solid #000;
            padding: 20px;
            width: 300px;
            margin: 20px auto;
            text-align: center;
          }
          .sku { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
          .title { font-size: 14px; margin-bottom: 15px; color: #333; }
          .barcode { font-family: 'Libre Barcode 39', monospace; font-size: 48px; margin: 10px 0; }
          .barcode-text { font-size: 12px; font-family: monospace; }
          @media print {
            body { margin: 0; padding: 10px; }
            .no-print { display: none; }
            .label { page-break-inside: avoid; }
          }
        </style>
        <link href="https://fonts.googleapis.com/css2?family=Libre+Barcode+39&display=swap" rel="stylesheet">
      </head>
      <body>
        ${labelsHtml}
        <div class="no-print" style="text-align: center; margin-top: 20px;">
          <button onclick="window.print()">Print All Labels</button>
          <button onclick="window.close()">Close</button>
        </div>
      </body>
      </html>
    `);
    labelWindow.document.close();
  };

  return (
    <div className="absolute inset-0 px-4 py-6 sm:px-6 lg:px-8 flex flex-col overflow-y-auto lg:overflow-hidden">
      {/* Filters - Compact on mobile */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-subtle p-3 lg:p-4 mb-4 lg:mb-6 sticky top-0 z-20">
        {/* Mobile: Compact filter row */}
        <div className="flex gap-2 lg:hidden">
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg"
          />
          <button
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className={`px-3 py-2 rounded-lg border ${showMobileFilters ? 'bg-brand-blue text-white border-brand-blue' : 'bg-white text-slate-600 border-slate-200'}`}
          >
            <FunnelIcon className="w-5 h-5" />
          </button>
          <button
            onClick={handleSyncProducts}
            disabled={syncing}
            className="px-3 py-2 text-sm font-medium text-white bg-brand-blue rounded-lg disabled:opacity-50"
          >
            {syncing ? '...' : 'Sync'}
          </button>
        </div>

        {/* Mobile: Expandable filters */}
        {showMobileFilters && (
          <div className="lg:hidden mt-3 pt-3 border-t border-slate-200 space-y-2">
            <select className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
              <option>All Products</option>
              <option>Candles</option>
              <option>Wax Melts</option>
              <option>Supplies</option>
            </select>
            {selectedProducts.length > 0 && (
              <button 
                onClick={handleBulkPrintLabels}
                className="w-full px-3 py-2 text-sm font-medium text-white bg-brand-blue rounded-lg"
              >
                Print {selectedProducts.length} Labels
              </button>
            )}
          </div>
        )}

        {/* Desktop: Full filter bar */}
        <div className="hidden lg:flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <input
              type="text"
              placeholder="Search by SKU, barcode, or title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-96 px-4 py-2 text-sm border border-slate-200 rounded-lg"
            />
            <select className="px-4 py-2 border border-slate-200 rounded-lg text-sm">
              <option>All Products</option>
              <option>Candles</option>
              <option>Wax Melts</option>
              <option>Supplies</option>
            </select>
          </div>

          <div className="flex items-center gap-3">
            {selectedProducts.length > 0 && (
              <button 
                onClick={handleBulkPrintLabels}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-blue hover:bg-brand-blue-hover rounded-lg"
              >
                Print {selectedProducts.length} Labels
              </button>
            )}
            <button
              onClick={handleSyncProducts}
              disabled={syncing}
              className="px-4 py-2 text-sm font-medium text-brand-blue bg-blue-50 hover:bg-blue-100 rounded-lg disabled:opacity-50"
            >
              {syncing ? 'Syncing...' : 'Sync Products'}
            </button>
          </div>
        </div>
      </div>

      {/* Products Grid/Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-subtle flex-1 min-h-0 flex flex-col">
        <div className="overflow-x-auto overflow-y-auto flex-1">
          <table className="w-full divide-y divide-slate-200" style={{ minWidth: '700px' }}>
          <thead className="bg-slate-50 sticky top-0 z-10 shadow-sm">
            <tr>
              <th className="px-6 py-3 text-left w-10">
                <input
                  type="checkbox"
                  className="rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedProducts(products.map((p) => p.id));
                    } else {
                      setSelectedProducts([]);
                    }
                  }}
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">
                Product
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">
                SKU
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">
                Barcode
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">
                Price
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">
                Weight
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {loading && (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-slate-600">
                  Loading products...
                </td>
              </tr>
            )}
            {!loading && products.map((product) => (
              <React.Fragment key={product.id}>
                <tr className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => toggleProductExpansion(product.id)}
                        className="text-slate-400 hover:text-slate-600"
                      >
                        {expandedProducts.has(product.id) ? '▼' : '▶'}
                      </button>
                      <input
                        type="checkbox"
                        checked={selectedProducts.includes(product.id)}
                        onChange={() => toggleProductSelection(product.id)}
                        className="rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
                      />
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt={product.title}
                          className="w-12 h-12 rounded object-cover mr-3"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center mr-3 text-xl">
                          🏷️
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          {product.title}
                        </p>
                        <p className="text-xs text-slate-500">
                          {productVariants[product.id]?.length || product.variant_count || 0} variant{((productVariants[product.id]?.length || product.variant_count || 0) !== 1) ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm font-mono text-slate-900">
                    {product.sku}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm font-mono text-slate-600">
                    {product.barcode || '-'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm font-medium text-slate-900">
                    ${product.price ? parseFloat(product.price.toString()).toFixed(2) : '0.00'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-slate-600">
                    {product.weight ? `${product.weight} oz` : '-'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <button 
                    onClick={() => handleEditProduct(product)}
                    className="text-sm text-brand-blue hover:text-brand-blue-hover font-medium mr-4"
                  >
                    Edit
                  </button>
                  <button 
                    onClick={() => handlePrintLabel(product.sku, product.title, product.barcode)}
                    className="text-sm text-brand-blue hover:text-brand-blue-hover font-medium"
                  >
                    Print Label
                  </button>
                </td>
              </tr>

              {/* Variants Row (Expandable) */}
              {expandedProducts.has(product.id) && productVariants[product.id] && (
                <tr className="bg-slate-50">
                  <td colSpan={6} className="px-6 py-4">
                    <div className="ml-8">
                      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                        Product Variants ({productVariants[product.id].length})
                      </h4>
                      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
                        <table className="w-full min-w-[600px]">
                          <thead className="bg-slate-50">
                            <tr className="text-xs text-slate-500 uppercase">
                              <th className="px-4 py-2 text-left font-medium whitespace-nowrap">Variant</th>
                              <th className="px-4 py-2 text-left font-medium whitespace-nowrap">SKU</th>
                              <th className="px-4 py-2 text-left font-medium whitespace-nowrap">Barcode</th>
                              <th className="px-4 py-2 text-right font-medium whitespace-nowrap">Price</th>
                              <th className="px-4 py-2 text-right font-medium whitespace-nowrap">Weight</th>
                              <th className="px-4 py-2 text-right font-medium whitespace-nowrap">Inventory</th>
                              <th className="px-4 py-2 text-center font-medium whitespace-nowrap">Packing</th>
                              <th className="px-4 py-2 text-right font-medium whitespace-nowrap">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {productVariants[product.id].map((variant) => (
                              <tr key={variant.id} className="hover:bg-slate-50">
                                <td className="px-4 py-2.5">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-slate-900 whitespace-nowrap">
                                      {variant.option1 || variant.title}
                                    </span>
                                    {variant.is_bundle && (
                                      <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-purple-100 text-purple-700 rounded whitespace-nowrap">
                                        BUNDLE
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-2.5 whitespace-nowrap">
                                  <span className="text-sm font-mono text-slate-700">{variant.sku}</span>
                                </td>
                                <td className="px-4 py-2.5 whitespace-nowrap">
                                  <span className="text-sm font-mono text-slate-600">{variant.barcode || '-'}</span>
                                </td>
                                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                                  <span className="text-sm font-medium text-slate-900">
                                    ${variant.price ? parseFloat(variant.price.toString()).toFixed(2) : '0.00'}
                                  </span>
                                </td>
                                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                                  <span className="text-sm text-slate-700">
                                    {variant.weight ? `${variant.weight} oz` : '-'}
                                  </span>
                                </td>
                                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                                  <span className="text-sm text-slate-700">{variant.inventory_quantity || 0}</span>
                                </td>
                                <td className="px-4 py-2.5 text-center whitespace-nowrap">
                                  <button
                                    onClick={() => handleToggleVariantPackingVerification(product.id, variant.id, variant.requires_packing_verification)}
                                    className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                                      variant.requires_packing_verification === true
                                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                        : variant.requires_packing_verification === false
                                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                    }`}
                                    title={
                                      variant.requires_packing_verification === true
                                        ? 'Requires packing (click to inherit)'
                                        : variant.requires_packing_verification === false
                                        ? 'Skip packing (click to require)'
                                        : `Inherit from product (${product.requires_packing_verification ? 'requires' : 'skip'})`
                                    }
                                  >
                                    {variant.requires_packing_verification === true
                                      ? '✓ Yes'
                                      : variant.requires_packing_verification === false
                                      ? '✗ No'
                                      : '↑ Inherit'}
                                  </button>
                                </td>
                                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                                  <div className="flex items-center justify-end gap-2">
                                    <button 
                                      onClick={() => handleConfigureBundle(variant, product)}
                                      className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                                        variant.is_bundle 
                                          ? 'bg-purple-100 text-purple-700 hover:bg-purple-200' 
                                          : 'text-slate-500 hover:text-purple-700 hover:bg-purple-50'
                                      }`}
                                      title={variant.is_bundle ? 'Edit Bundle' : 'Configure as Bundle'}
                                    >
                                      <CubeIcon className="w-4 h-4" />
                                    </button>
                                    <button 
                                      onClick={() => handlePrintLabel(variant.sku, `${product.title} - ${variant.option1 || variant.title}`, variant.barcode)}
                                      className="px-2 py-1 text-xs font-medium text-brand-blue hover:text-brand-blue-hover hover:bg-blue-50 rounded transition-colors"
                                      title="Print Label"
                                    >
                                      🖨️
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Edit Product Modal */}
      {editingProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-slate-900">Edit Product</h2>
                <button 
                  onClick={() => setEditingProduct(null)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex items-center space-x-4 mb-4">
                {editingProduct.image_url ? (
                  <img 
                    src={editingProduct.image_url} 
                    alt={editingProduct.title}
                    className="w-16 h-16 rounded object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 bg-slate-100 rounded flex items-center justify-center text-2xl">
                    🏷️
                  </div>
                )}
                <div>
                  <h3 className="font-medium text-slate-900">{editingProduct.title}</h3>
                  <p className="text-sm text-slate-500">SKU: {editingProduct.sku}</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Weight (oz)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={editForm.weight}
                  onChange={(e) => setEditForm(prev => ({ ...prev, weight: e.target.value }))}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  placeholder="Enter weight in ounces"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Barcode
                </label>
                <input
                  type="text"
                  value={editForm.barcode}
                  onChange={(e) => setEditForm(prev => ({ ...prev, barcode: e.target.value }))}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  placeholder="Enter barcode"
                />
              </div>

              {/* Packing Verification */}
              <div className="flex items-center justify-between py-3 border-t border-slate-100">
                <div>
                  <label className="text-sm font-medium text-slate-700">
                    Require Packing Verification
                  </label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Items will need to be scanned at the packing station
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={editingProduct.requires_packing_verification || false}
                  onChange={() => handleTogglePackingVerification(editingProduct.id, !editingProduct.requires_packing_verification)}
                  className="rounded border-slate-300 text-brand-blue focus:ring-brand-blue cursor-pointer h-5 w-5"
                />
              </div>

              {/* International Shipping Section */}
              <div className="border-t border-slate-200 pt-4 mt-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">International Shipping (Customs)</h4>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      HS Tariff Code
                    </label>
                    <input
                      type="text"
                      value={editForm.hs_tariff_code}
                      onChange={(e) => setEditForm(prev => ({ ...prev, hs_tariff_code: e.target.value }))}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                      placeholder="e.g., 3406.00.00"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Required for international shipments.{' '}
                      <a 
                        href="https://hts.usitc.gov/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-brand-blue hover:underline"
                      >
                        Look up HS codes
                      </a>
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Country of Origin
                    </label>
                    <select
                      value={editForm.country_of_origin}
                      onChange={(e) => setEditForm(prev => ({ ...prev, country_of_origin: e.target.value }))}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    >
                      {COUNTRIES.map(c => (
                        <option key={c.code} value={c.code}>{c.name} ({c.code})</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="text-sm text-slate-500 pt-2">
                <p>Note: Price and title are synced from Shopify. Edit those fields in your Shopify admin.</p>
              </div>
            </div>
            
            <div className="p-6 border-t border-slate-200 flex justify-end space-x-3">
              <button 
                onClick={() => setEditingProduct(null)}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800"
              >
                Cancel
              </button>
              <button 
                onClick={handleSaveProduct}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-blue hover:bg-brand-blue-hover rounded-lg disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bundle Configuration Modal */}
      {/* Variant Picker Modal */}
      {showVariantPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-slate-900">Select Product Variant</h3>
                <button
                  onClick={() => setShowVariantPicker(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  ✕
                </button>
              </div>
              {/* Search Input */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by product name, SKU, or barcode..."
                  value={variantSearchQuery}
                  onChange={(e) => setVariantSearchQuery(e.target.value)}
                  className="w-full px-4 py-2.5 pl-10 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  autoFocus
                />
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            
            {/* Variant List */}
            <div className="flex-1 overflow-y-auto p-2">
              {allVariants
                .filter(v => {
                  if (!variantSearchQuery) return true;
                  const q = variantSearchQuery.toLowerCase();
                  return (
                    v.productTitle.toLowerCase().includes(q) ||
                    v.title.toLowerCase().includes(q) ||
                    v.sku.toLowerCase().includes(q) ||
                    (v.barcode && v.barcode.toLowerCase().includes(q))
                  );
                })
                .slice(0, 100) // Limit results for performance
                .map((variant) => (
                  <button
                    key={variant.id}
                    onClick={() => {
                      setSelectedComponentId(variant.id);
                      setShowVariantPicker(false);
                    }}
                    className="w-full text-left p-3 rounded-lg hover:bg-purple-50 border border-transparent hover:border-purple-200 transition-colors mb-1"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center text-slate-400">
                        <CubeIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {variant.productTitle}
                        </p>
                        <p className="text-xs text-slate-600">
                          {variant.title !== 'Default' && <span className="font-medium">{variant.title} • </span>}
                          SKU: {variant.sku}
                        </p>
                        {variant.barcode && (
                          <p className="text-xs text-slate-500 font-mono">
                            Barcode: {variant.barcode}
                          </p>
                        )}
                      </div>
                      <div className="text-purple-600">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </div>
                    </div>
                  </button>
                ))}
              
              {loadingBundle && (
                <div className="text-center py-8">
                  <div className="animate-spin h-8 w-8 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                  <p className="text-sm text-slate-500">Loading variants...</p>
                </div>
              )}
              
              {bundleError && (
                <div className="m-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700 font-medium">Error loading variants</p>
                  <p className="text-xs text-red-600 mt-1">{bundleError}</p>
                  <button
                    onClick={() => configuringBundle && handleConfigureBundle(configuringBundle.variant, configuringBundle.product)}
                    className="mt-2 px-3 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-700 rounded"
                  >
                    Retry
                  </button>
                </div>
              )}
              
              {!loadingBundle && !bundleError && allVariants.filter(v => {
                if (!variantSearchQuery) return true;
                const q = variantSearchQuery.toLowerCase();
                return (
                  v.productTitle.toLowerCase().includes(q) ||
                  v.title.toLowerCase().includes(q) ||
                  v.sku.toLowerCase().includes(q) ||
                  (v.barcode && v.barcode.toLowerCase().includes(q))
                );
              }).length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <p className="text-sm">No variants found{variantSearchQuery ? ` matching "${variantSearchQuery}"` : ''}</p>
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-slate-200 bg-slate-50 text-center">
              <p className="text-xs text-slate-500">
                {loadingBundle ? 'Loading...' : `${allVariants.length} variants available`} • Type to search
              </p>
            </div>
          </div>
        </div>
      )}

      {configuringBundle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                    <CubeIcon className="w-6 h-6 text-purple-600" />
                    Configure Bundle
                  </h2>
                  <p className="text-sm text-slate-500 mt-1">
                    {configuringBundle.product.title} - {configuringBundle.variant.option1 || configuringBundle.variant.title}
                  </p>
                  <p className="text-xs text-slate-400 font-mono mt-0.5">
                    SKU: {configuringBundle.variant.sku}
                  </p>
                </div>
                <button 
                  onClick={() => setConfiguringBundle(null)}
                  className="text-slate-400 hover:text-slate-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              {loadingBundle ? (
                <div className="text-center py-8 text-slate-500">
                  Loading bundle configuration...
                </div>
              ) : (
                <>
                  {/* Current Bundle Components */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">
                      Bundle Components
                      <span className="text-xs font-normal text-slate-500 ml-2">
                        (Items that will be scanned during packing)
                      </span>
                    </h3>
                    
                    {bundleComponents.length === 0 ? (
                      <div className="bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                        <CubeIcon className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                        <p className="text-sm text-slate-600">No components configured</p>
                        <p className="text-xs text-slate-500 mt-1">
                          Add components below to define what makes up this bundle
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {bundleComponents.map((component) => (
                          <div
                            key={component.id}
                            className="flex items-center justify-between bg-purple-50 border border-purple-200 rounded-lg p-3"
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                <span className="text-lg font-bold text-purple-600">
                                  {component.quantity}×
                                </span>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-slate-900">
                                  {component.product_title}
                                </p>
                                <p className="text-xs text-slate-600">
                                  {component.variant_title} • SKU: {component.sku}
                                </p>
                                {component.barcode && (
                                  <p className="text-xs text-slate-500 font-mono">
                                    Barcode: {component.barcode}
                                  </p>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveBundleComponent(component.id)}
                              className="px-3 py-1 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                        
                        {/* Summary */}
                        <div className="mt-4 p-3 bg-purple-100 rounded-lg">
                          <p className="text-sm font-medium text-purple-800">
                            📦 When packing this item, scan {bundleComponents.reduce((sum, c) => sum + c.quantity, 0)} barcode(s) total
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Add Component Form */}
                  <div className="border-t border-slate-200 pt-6">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">
                      Add Component
                    </h3>
                    <div className="flex gap-3 items-end">
                      <div className="flex-1 min-w-0">
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Component Product
                        </label>
                        {selectedComponentId ? (
                          <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg">
                            <div className="flex-1 min-w-0 overflow-hidden">
                              <p className="text-sm font-medium text-slate-900 truncate">
                                {allVariants.find(v => v.id === selectedComponentId)?.productTitle} - {allVariants.find(v => v.id === selectedComponentId)?.title}
                              </p>
                              <p className="text-xs text-slate-600 truncate">
                                SKU: {allVariants.find(v => v.id === selectedComponentId)?.sku}
                              </p>
                            </div>
                            <button
                              onClick={() => setSelectedComponentId('')}
                              className="text-slate-400 hover:text-slate-600 flex-shrink-0"
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setVariantSearchQuery('');
                              setShowVariantPicker(true);
                            }}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-left text-slate-500 hover:border-purple-300 hover:bg-purple-50 transition-colors"
                          >
                            🔍 Search for a product variant...
                          </button>
                        )}
                      </div>
                      <div className="w-20 flex-shrink-0">
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Qty
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={componentQuantity}
                          onChange={(e) => setComponentQuantity(parseInt(e.target.value) || 1)}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-center"
                        />
                      </div>
                      <button
                        onClick={() => {
                          handleAddBundleComponent();
                          setSelectedComponentId('');
                        }}
                        disabled={!selectedComponentId || savingBundle}
                        className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {savingBundle ? 'Adding...' : 'Add'}
                      </button>
                    </div>
                  </div>

                  {/* Help Text */}
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <h4 className="text-sm font-semibold text-blue-800 mb-1">
                      💡 How Bundles Work
                    </h4>
                    <ul className="text-xs text-blue-700 space-y-1">
                      <li>• When an order contains this bundle SKU, the packing station will require scanning the component items instead</li>
                      <li>• Example: "5 Gallon" bundle with 5× "1 Gallon" components requires scanning 5 individual 1-gallon barcodes</li>
                      <li>• This ensures the correct physical items are packed for every order</li>
                    </ul>
                  </div>
                </>
              )}
            </div>
            
            <div className="p-6 border-t border-slate-200 flex justify-end">
              <button 
                onClick={() => setConfiguringBundle(null)}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-blue hover:bg-brand-blue-hover rounded-lg"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
